import { useCallback } from "react";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import { useEditor } from "../../../hooks/useEditor";
import { useGoogleAnalytics } from "../../../hooks/useGoogleAnalytics";
import { showPhotoRemoveConfirmDialogAsync } from "../../photo-selection-screen/remove-confirm-dialog";

interface IGoogleAnalyticsEvents {
	containerAction: eXpress.GaTrackingAction;
	confirmAction: eXpress.GaTrackingAction;
	cancelAction: eXpress.GaTrackingAction;
}

/**
 * Hook to delete photos from photo selection and definition with confirmation dialog and optional analytics tracking.
 */
export function useDeleteFromPhotoSelection() {
	const googleAnalytics = useGoogleAnalytics();
	const editor = useEditor();
	const dashboard = useDashboardContext();

	const removeFromPhotoSelectionWithDialog = useCallback(
		async (
			photos: readonly eXpress.core.Photo[],
			analyticsEvents?: IGoogleAnalyticsEvents,
		): Promise<boolean> => {
			const { containerAction, confirmAction, cancelAction } = analyticsEvents || {};

			if (containerAction) {
				googleAnalytics.trackEventToGa({
					trackingAction: containerAction,
				} as eXpress.GaEventInfo);
			}

			const photosToRemove = photos;

			const canDelete = await showPhotoRemoveConfirmDialogAsync(
				photosToRemove.length,
				photosToRemove.filter((p) => p.isUsed).length,
			);

			if (!canDelete) {
				if (cancelAction) {
					googleAnalytics.trackEventToGa({
						trackingAction: cancelAction,
					} as eXpress.GaEventInfo);
				}
				return false;
			}

			if (confirmAction) {
				googleAnalytics.trackEventToGa({
					trackingAction: confirmAction,
				} as eXpress.GaEventInfo);
			}

			// Remove selected photos from the definition
			const zonesContainingPhotosToRemove = editor.definition
				? editor.definition.getAllZones().filter((zone) => {
						// Remove from PhotZones and Page background
						const photo = eXpress.core.isPhotoLookZone(zone)
							? zone.getPhotoLook()?.photo
							: undefined;
						if (!photo) return false;
						return photosToRemove.includes(photo);
				  })
				: [];
			dashboard.removePhotoFromZones(zonesContainingPhotosToRemove, true);

			// Remove from Photo-selection list
			// Do after removing photos from definition, because it only will removing unused photos
			return editor.removeFromPhotoSelection(photosToRemove);
		},
		[dashboard, editor, googleAnalytics],
	);

	return removeFromPhotoSelectionWithDialog;
}
